import { useQuery } from '@tanstack/react-query';
import { api, egp, type Plan, type PlanFeaturePoint } from '../api';
import { Card, PageLoader } from './ui';
import { PlanBenefitList } from './PlanBenefitList';
import { ChoosePlanCta } from './ChoosePlanCta';
import { PlanOfferBadge, PlanPopularBadge, isMostPopularPlan } from './PlanOfferBadge';

/**
 * Inline plan picker shown when a student needs a subscription.
 * Never link away to /plans — show plans here with "Choose this plan" → checkout.
 */
export function InlinePlans({
  heading,
  subtext,
}: {
  heading?: string;
  subtext?: string;
} = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<{ plans: Plan[]; sharedPoints: PlanFeaturePoint[] }>('/api/plans'),
  });

  const plans = data?.plans ?? [];
  const sharedPoints = data?.sharedPoints ?? [];

  return (
    <div>
      {(heading || subtext) && (
        <div className="mb-6 text-center sm:text-left">
          {heading && (
            <h2 className="font-heading text-2xl font-bold text-ink mb-2">{heading}</h2>
          )}
          {subtext && <p className="text-ink-soft text-sm">{subtext}</p>}
        </div>
      )}

      {isLoading ? (
        <PageLoader variant="inline" />
      ) : plans.length === 0 ? (
        <p className="text-center text-ink-soft text-sm py-8">No plans available right now.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {plans.map((plan, i) => (
            <Card
              key={plan.id}
              className={`p-5 flex flex-col relative overflow-hidden rounded-2xl ${
                isMostPopularPlan(i) ? 'ring-2 ring-sand shadow-pop bg-teal-soft' : ''
              }`}
            >
              {isMostPopularPlan(i) && <PlanPopularBadge />}
              <PlanOfferBadge />
              <div className="font-heading font-bold text-xl text-ink mb-1 pt-14 pr-4">{plan.name}</div>
              <div className="mb-4">
                {plan.compareAtPriceEgp > plan.priceEgp && (
                  <span className="text-sm line-through text-ink-faint mr-2">
                    {egp(plan.compareAtPriceEgp)}
                  </span>
                )}
                <span className="text-4xl font-bold text-sand">{egp(plan.priceEgp)}</span>
              </div>
              {plan.description && (
                <p className="text-sm text-ink-soft mb-3">{plan.description}</p>
              )}
              <PlanBenefitList plan={plan} sharedPoints={sharedPoints} />
              <ChoosePlanCta
                planId={plan.id}
                className={`text-center text-sm font-semibold py-3.5 rounded-radius-sm hover:opacity-90 transition-opacity touch-manipulation min-h-[48px] flex items-center justify-center w-full ${
                  isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'
                }`}
              >
                Choose this plan
              </ChoosePlanCta>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-ink-faint mt-5 text-center">
        Pay once. No monthly fees.
      </p>
    </div>
  );
}
